import SalesDashboardAPI from 'dashboard/api/salesDashboard';
import camelcaseKeys from 'camelcase-keys';

const initialState = {
  summary: {},
  pipelineValue: {},
  revenueForecast: {},
  conversionMetrics: {},
  agentPerformance: {},
  companyLeaderboard: {},
  uiFlags: {
    fetchingSummary: false,
    fetchingPipeline: false,
    fetchingForecast: false,
    fetchingConversion: false,
    fetchingAgentPerformance: false,
    fetchingCompanyLeaderboard: false,
    fetchingAll: false,
  },
  filters: {
    since: null,
    until: null,
    period: 'month',
    funnelIds: [],
    agentIds: [],
    companyIds: [],
  },
};

const getters = {
  getSummary: $state => camelcaseKeys($state.summary, { deep: true }),
  getPipelineValue: $state =>
    camelcaseKeys($state.pipelineValue, { deep: true }),
  getRevenueForecast: $state =>
    camelcaseKeys($state.revenueForecast, { deep: true }),
  getConversionMetrics: $state =>
    camelcaseKeys($state.conversionMetrics, { deep: true }),
  getAgentPerformance: $state =>
    camelcaseKeys($state.agentPerformance, { deep: true }),
  getCompanyLeaderboard: $state =>
    camelcaseKeys($state.companyLeaderboard, { deep: true }),
  getUIFlags: $state => $state.uiFlags,
  getFilters: $state => $state.filters,

  // Computed metrics
  getTotalPipelineValue: $state => {
    return $state.summary?.total_pipeline_value || 0;
  },
  getWinRate: $state => {
    return $state.summary?.win_rate || 0;
  },
  getOpenDealsCount: $state => {
    return $state.summary?.open_deals || 0;
  },
};

const mutations = {
  SET_SUMMARY($state, data) {
    $state.summary = data;
  },
  SET_PIPELINE_VALUE($state, data) {
    $state.pipelineValue = data;
  },
  SET_REVENUE_FORECAST($state, data) {
    $state.revenueForecast = data;
  },
  SET_CONVERSION_METRICS($state, data) {
    $state.conversionMetrics = data;
  },
  SET_AGENT_PERFORMANCE($state, data) {
    $state.agentPerformance = data;
  },
  SET_COMPANY_LEADERBOARD($state, data) {
    $state.companyLeaderboard = data;
  },
  SET_UI_FLAG($state, { flag, value }) {
    $state.uiFlags = { ...$state.uiFlags, [flag]: value };
  },
  SET_FILTERS($state, filters) {
    $state.filters = { ...$state.filters, ...filters };
  },
  CLEAR_ALL($state) {
    $state.summary = {};
    $state.pipelineValue = {};
    $state.revenueForecast = {};
    $state.conversionMetrics = {};
    $state.agentPerformance = {};
    $state.companyLeaderboard = {};
  },
};

const actions = {
  async fetchSummary({ commit, state: $state }) {
    commit('SET_UI_FLAG', { flag: 'fetchingSummary', value: true });
    try {
      const { data } = await SalesDashboardAPI.getSummary($state.filters);
      commit('SET_SUMMARY', data);
    } catch {
      // Error handling
    } finally {
      commit('SET_UI_FLAG', { flag: 'fetchingSummary', value: false });
    }
  },

  async fetchPipelineValue({ commit, state: $state }) {
    commit('SET_UI_FLAG', { flag: 'fetchingPipeline', value: true });
    try {
      const { data } = await SalesDashboardAPI.getPipelineValue($state.filters);
      commit('SET_PIPELINE_VALUE', data);
    } catch {
      // Error handling
    } finally {
      commit('SET_UI_FLAG', { flag: 'fetchingPipeline', value: false });
    }
  },

  async fetchRevenueForecast({ commit, state: $state }) {
    commit('SET_UI_FLAG', { flag: 'fetchingForecast', value: true });
    try {
      const { data } = await SalesDashboardAPI.getRevenueForecast(
        $state.filters
      );
      commit('SET_REVENUE_FORECAST', data);
    } catch {
      // Error handling
    } finally {
      commit('SET_UI_FLAG', { flag: 'fetchingForecast', value: false });
    }
  },

  async fetchConversionMetrics({ commit, state: $state }) {
    commit('SET_UI_FLAG', { flag: 'fetchingConversion', value: true });
    try {
      const { data } = await SalesDashboardAPI.getConversionMetrics(
        $state.filters
      );
      commit('SET_CONVERSION_METRICS', data);
    } catch {
      // Error handling
    } finally {
      commit('SET_UI_FLAG', { flag: 'fetchingConversion', value: false });
    }
  },

  async fetchAgentPerformance({ commit, state: $state }) {
    commit('SET_UI_FLAG', { flag: 'fetchingAgentPerformance', value: true });
    try {
      const { data } = await SalesDashboardAPI.getAgentPerformance(
        $state.filters
      );
      commit('SET_AGENT_PERFORMANCE', data);
    } catch {
      // Error handling
    } finally {
      commit('SET_UI_FLAG', { flag: 'fetchingAgentPerformance', value: false });
    }
  },

  async fetchCompanyLeaderboard({ commit, state: $state }) {
    commit('SET_UI_FLAG', { flag: 'fetchingCompanyLeaderboard', value: true });
    try {
      const { data } = await SalesDashboardAPI.getCompanyLeaderboard(
        $state.filters
      );
      commit('SET_COMPANY_LEADERBOARD', data);
    } catch {
      // Error handling
    } finally {
      commit('SET_UI_FLAG', {
        flag: 'fetchingCompanyLeaderboard',
        value: false,
      });
    }
  },

  async fetchAllDashboardData({ commit, dispatch }) {
    commit('SET_UI_FLAG', { flag: 'fetchingAll', value: true });
    try {
      await Promise.all([
        dispatch('fetchSummary'),
        dispatch('fetchPipelineValue'),
        dispatch('fetchRevenueForecast'),
        dispatch('fetchConversionMetrics'),
        dispatch('fetchAgentPerformance'),
        dispatch('fetchCompanyLeaderboard'),
      ]);
    } finally {
      commit('SET_UI_FLAG', { flag: 'fetchingAll', value: false });
    }
  },

  updateFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    dispatch('fetchAllDashboardData');
  },

  clearDashboard({ commit }) {
    commit('CLEAR_ALL');
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};
